import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { analystAPI } from '../../utils/api';
import { useAuth } from '../../contexts/AuthContext';

const GRADE_LABELS = {
  tradeValue: { creator_wins: 'You win the value', even: 'Even value', recipient_wins: 'They win the value' },
  timelineFit: { good_fit: 'Good fit', questionable: 'Questionable', wrong_direction: 'Wrong direction' },
  capitalImpact: { net_gain: 'Net gain', neutral: 'Neutral', net_loss: 'Net loss' },
};
const GRADE_DIM_LABEL = { tradeValue: 'Trade value', timelineFit: 'Timeline fit', capitalImpact: 'Capital impact' };

const WINDOW_LABELS = { contend: 'Contend', retool: 'Retool', rebuild: 'Rebuild' };
const ROSTER_POSITION_LABELS = [['qb', 'QB'], ['rb', 'RB'], ['wr', 'WR'], ['te', 'TE'], ['picks', 'Picks']];

const TypeBadge = ({ type }) => {
  const labels = { trade: 'Trade', hypothetical: 'Hypothetical', question: 'Question', roster: 'Roster review' };
  return (
    <span style={{
      display: 'inline-block',
      background: 'var(--ft-surface)', border: '1px solid var(--ft-hairline)',
      borderRadius: 4, padding: '2px 8px',
      fontSize: 10, fontWeight: 700, color: 'var(--ft-ink-3)',
      letterSpacing: '0.06em', textTransform: 'uppercase',
      marginRight: 8,
    }}>
      {labels[type] || type}
    </span>
  );
};

const itemSummary = (item) => {
  if (item.type === 'roster') return 'Your full roster, graded';
  if (item.type === 'trade') return 'Trade from your package';
  if (item.type === 'hypothetical') {
    const giving = (item.payload?.giving || []).map((p) => p.name).join(', ');
    const getting = (item.payload?.getting || []).map((p) => p.name).join(', ');
    if (giving || getting) return `${giving || '—'} → ${getting || '—'}`;
    return 'Hypothetical trade';
  }
  if (item.type === 'question') return item.question || 'Question';
  return 'Item';
};

const AnalystReviewResult = () => {
  const { reviewId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [review, setReview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!user) return;
    analystAPI.getReview(reviewId)
      .then((r) => setReview(r.data))
      .catch((err) => setError(err?.response?.data?.error || 'Review not found.'))
      .finally(() => setLoading(false));
  }, [user, reviewId]);

  if (loading) {
    return (
      <div className="ft-view" style={{ display: 'flex', justifyContent: 'center', paddingTop: 120 }}>
        <span style={{ color: 'var(--ft-ink-3)', fontSize: 15 }}>Loading…</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="ft-view">
        <p style={{ color: 'var(--ft-red)', fontSize: 15 }}>{error}</p>
      </div>
    );
  }

  const analyst = review?.analyst;
  const items = review?.items || [];
  const isPending = review?.status === 'awaiting_review';
  const isCompleted = review?.status === 'completed';

  return (
    <div className="ft-view">
      <div style={{ maxWidth: 680, margin: '0 auto' }}>

        {/* Header */}
        <div style={{ marginBottom: 48 }}>
          <span className="ft-k">{review?.productType === 'roster_review' ? 'Roster Review' : 'Expert Review'}</span>
          <h1 className="ft-hero" style={{ marginTop: 8, marginBottom: 8 }}>
            {isCompleted ? 'Your review is in.' : 'Review pending.'}
          </h1>
          {analyst && (
            <p style={{ fontSize: 15, color: 'var(--ft-ink-3)', margin: 0 }}>
              {analyst.name}
              {isPending && ` · Will respond within ${analyst.slaHours}h`}
              {isCompleted && review.submittedAt && ` · Completed ${new Date(review.submittedAt).toLocaleDateString('en-US', { month: 'long', day: 'numeric' })}`}
            </p>
          )}
        </div>

        {/* Overall verdict card */}
        {isCompleted && review?.grades && Object.values(review.grades).some(Boolean) && (
          <div style={{
            background: 'var(--ft-green-soft)', border: '1px solid rgba(52,199,89,0.25)',
            borderRadius: 18, padding: '24px 28px', marginBottom: 32,
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 20, flexWrap: 'wrap' }}>
              {review.grades.overallGrade && (
                <div style={{
                  width: 72, height: 72, borderRadius: '50%',
                  background: 'var(--ft-dark)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                }}>
                  <span style={{ fontSize: 32, fontWeight: 800, color: '#fff', letterSpacing: '-0.02em' }}>
                    {review.grades.overallGrade}
                  </span>
                </div>
              )}
              <div style={{ flex: 1, minWidth: 200 }}>
                <span className="ft-k" style={{ color: 'var(--ft-green-ink)', display: 'block', marginBottom: 10 }}>
                  {analyst?.name || 'Analyst'}'s verdict
                </span>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px 20px', alignItems: 'center' }}>
                  {review.grades.window && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--ft-ink)' }}>Contention window</span>
                      <span style={{ background: 'var(--ft-dark)', color: '#fff', borderRadius: 'var(--ft-r-pill)', padding: '4px 11px', fontSize: 11.5, fontWeight: 700 }}>
                        {WINDOW_LABELS[review.grades.window] || review.grades.window}
                      </span>
                    </div>
                  )}
                  {['tradeValue', 'timelineFit', 'capitalImpact'].map((dim) => {
                    const v = review.grades[dim];
                    if (!v) return null;
                    return (
                      <div key={dim}>
                        <span className="ft-k" style={{ display: 'block', marginBottom: 2 }}>{GRADE_DIM_LABEL[dim]}</span>
                        <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--ft-ink)' }}>{GRADE_LABELS[dim]?.[v] || v}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Items */}
        {items.map((item, idx) => (
          <div key={item.id} style={{ marginBottom: 24 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
              <TypeBadge type={item.type} />
              <span style={{ fontSize: 13, color: 'var(--ft-ink-3)' }}>Item {idx + 1} of {items.length}</span>
            </div>
            <div className="ft-card">
              <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16, marginBottom: 8 }}>
                <p style={{ fontSize: 16, fontWeight: 600, color: 'var(--ft-ink)', lineHeight: 1.4, flex: 1, margin: 0 }}>
                  {itemSummary(item)}
                </p>
                {item.grades?.overallGrade && (
                  <div style={{
                    width: 44, height: 44, borderRadius: '50%',
                    background: 'var(--ft-dark)', flexShrink: 0,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: 17, fontWeight: 900, color: '#fff', letterSpacing: '-0.02em',
                  }}>{item.grades.overallGrade}</div>
                )}
              </div>

              {item.type === 'hypothetical' && item.payload?.note && (
                <p style={{ fontSize: 14, color: 'var(--ft-ink-3)', margin: '0 0 8px', fontStyle: 'italic' }}>
                  "{item.payload.note}"
                </p>
              )}

              {item.type === 'roster' && item.question && (
                <p style={{ fontSize: 14, color: 'var(--ft-ink-3)', margin: '0 0 8px', fontStyle: 'italic' }}>
                  "{item.question}"
                </p>
              )}

              {item.type === 'roster' && item.grades && (
                <>
                  {item.grades.window && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 12 }}>
                      <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--ft-ink)' }}>Contention window</span>
                      <span style={{ background: 'var(--ft-dark)', color: '#fff', borderRadius: 'var(--ft-r-pill)', padding: '4px 11px', fontSize: 11.5, fontWeight: 700 }}>
                        {WINDOW_LABELS[item.grades.window] || item.grades.window}
                      </span>
                    </div>
                  )}
                  {item.grades.positions && Object.values(item.grades.positions).some(Boolean) && (
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(72px, 1fr))', gap: 8, marginTop: 14 }}>
                      {ROSTER_POSITION_LABELS.map(([key, label]) => {
                        const g = item.grades.positions?.[key];
                        if (!g) return null;
                        return (
                          <div key={key} style={{ background: '#fff', border: '1px solid var(--ft-hairline)', borderRadius: 12, padding: '12px 8px 10px', textAlign: 'center' }}>
                            <span className="ft-k" style={{ display: 'block', marginBottom: 5, fontSize: 10 }}>{label}</span>
                            <b style={{ fontFamily: "'SF Mono', ui-monospace, Menlo, monospace", fontSize: 19, fontWeight: 700, letterSpacing: '-0.02em', color: 'var(--ft-ink)' }}>{g}</b>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </>
              )}

              {item.grades && (item.grades.tradeValue || item.grades.timelineFit || item.grades.capitalImpact) && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px 20px', marginTop: 12 }}>
                  {['tradeValue', 'timelineFit', 'capitalImpact'].map((dim) => {
                    const v = item.grades[dim];
                    if (!v) return null;
                    return (
                      <div key={dim}>
                        <span className="ft-k" style={{ display: 'block', marginBottom: 2 }}>{GRADE_DIM_LABEL[dim]}</span>
                        <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--ft-ink)' }}>{GRADE_LABELS[dim]?.[v] || v}</span>
                      </div>
                    );
                  })}
                </div>
              )}

              {isCompleted && item.analystResponse && (
                <>
                  <div style={{ height: 1, background: 'var(--ft-hairline)', margin: '20px 0' }} />
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
                    <div style={{
                      width: 28, height: 28, borderRadius: '50%',
                      background: 'var(--ft-dark)', flexShrink: 0,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: 12, fontWeight: 800, color: '#fff',
                    }}>{analyst?.name?.[0] || 'A'}</div>
                    <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--ft-ink-2)' }}>
                      {analyst?.name || 'Analyst'}
                    </span>
                  </div>
                  <p style={{ fontSize: 15, color: 'var(--ft-ink)', lineHeight: 1.65, margin: 0, whiteSpace: 'pre-wrap' }}>
                    {item.analystResponse}
                  </p>
                  {item.type === 'roster' && (item.grades?.recommendedMoves || []).length > 0 && (
                    <div style={{ marginTop: 16, paddingTop: 16, borderTop: '1px solid var(--ft-hairline)' }}>
                      <span className="ft-k" style={{ display: 'block', marginBottom: 8 }}>Recommended moves</span>
                      <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: 6 }}>
                        {item.grades.recommendedMoves.map((m, i) => (
                          <li key={i} style={{ display: 'flex', gap: 9, fontSize: 14, lineHeight: 1.5, color: 'var(--ft-ink)' }}>
                            <span style={{ width: 4, height: 4, borderRadius: '50%', background: 'var(--ft-green)', flexShrink: 0, marginTop: 8 }} />
                            {m}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </>
              )}

              {isPending && (
                <>
                  <div style={{ height: 1, background: 'var(--ft-hairline)', margin: '20px 0' }} />
                  <p style={{ fontSize: 13, color: 'var(--ft-ink-3)', margin: 0 }}>Response pending.</p>
                </>
              )}
            </div>
          </div>
        ))}

        {/* Actions */}
        {isCompleted && (
          <div style={{ marginTop: 48, display: 'flex', flexDirection: 'column', gap: 10 }}>
            <button
              onClick={() => navigate(`/analyst/${analyst?.slug}`)}
              className="ft-card"
              style={{ cursor: 'pointer', textAlign: 'left', border: '1px solid var(--ft-hairline)', padding: '16px 20px' }}
            >
              <span style={{ fontSize: 14, fontWeight: 500, color: 'var(--ft-ink-2)' }}>Submit another review</span>
            </button>
            <button
              onClick={() => navigate('/trade-packages/dashboard')}
              className="ft-card"
              style={{ cursor: 'pointer', textAlign: 'left', border: '1px solid var(--ft-hairline)', padding: '16px 20px' }}
            >
              <span style={{ fontSize: 14, fontWeight: 500, color: 'var(--ft-ink-2)' }}>Your trade packages</span>
            </button>
          </div>
        )}

      </div>
    </div>
  );
};

export default AnalystReviewResult;
